import { useEffect, useRef } from 'react';
import { useAuth, useOrganization, useUser } from '@clerk/clerk-react';

import { clearGaIdentity, setGaIdentityFromSignup } from '@/lib/analytics/ga';

/**
 * Keeps GA4 session fields in sync with Clerk as they resolve: name, email,
 * then organization / account name. Clears on sign-out so the next merchant
 * on a shared machine does not inherit the previous identity.
 */
export function useGaIdentity(): void {
  const { userId } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    if (!userId) {
      if (lastKey.current) {
        lastKey.current = null;
        clearGaIdentity();
      }
      return;
    }
    if (!user) return;
    const key = `${userId}:${organization?.id ?? ''}`;
    if (lastKey.current === key) return;
    lastKey.current = key;
    const orgName = organization?.name ?? undefined;
    setGaIdentityFromSignup({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.primaryEmailAddress?.emailAddress,
      accountName: orgName,
      organization: orgName,
      userId,
    });
  }, [userId, user, organization?.id, organization?.name]);
}
